import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const VOID_BG = '#080810';
const CARD_BG = '#0D0D1A';
const CYAN = '#00FFE5';
const ICE = '#E8F4FF';
const MUTED = '#8899AA';
const GOLD = '#FFD700';
const MONO = "'JetBrains Mono', 'Courier New', monospace";
const ORBITRON = "'Orbitron', monospace";

const AIShowcase = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const showcaseSlides = [
    {
      id: 1,
      title: "82+ AI AGENTS STANDING BY",
      description: "From execution planning to content pipelines — specialized agents ready to run your missions.",
      features: ['Mission Planning', 'Autonomous Execution', 'Results Delivered', 'Full Run History'],
      agent: { name: 'COMMAND BRAIN', status: 'ONLINE', message: 'State your mission. I handle the rest.' }
    },
    {
      id: 2,
      title: "TRACK YOUR EXECUTION",
      description: "Monitor every run with intelligent insights and complete audit trails.",
      features: ['Goal Tracking', 'Progress Analytics', 'Milestone Logging', 'Action Cards'],
      agent: { name: 'VORTEX', status: 'ACTIVE', message: 'Mission analysis complete. Ready to execute.' }
    },
    {
      id: 3,
      title: "SHIP WITHOUT FRICTION",
      description: "Transform intent into ready-to-run workflows. Approve. Execute. Move.",
      features: ['Content Pipelines', 'Launch Sequences', 'Sales Outreach', 'Project Mapping'],
      agent: { name: 'OPERATIVE', status: 'STANDBY', message: 'Awaiting your directive, Commander.' }
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % showcaseSlides?.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [showcaseSlides?.length]);

  const currentSlideData = showcaseSlides?.[currentSlide];

  return (
    <div className="relative h-full overflow-hidden" style={{ background: '#0D0D1A' }}>
      {/* Star particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)]?.map((_, i) => (
          <div key={i} style={{
            position: 'absolute',
            width: Math.random() > 0.7 ? '2px' : '1px',
            height: Math.random() > 0.7 ? '2px' : '1px',
            background: CYAN,
            borderRadius: '50%',
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            opacity: Math.random() * 0.5 + 0.1,
            animation: `pulse ${2 + Math.random() * 3}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 3}s`,
          }} />
        ))}
      </div>
      <div className="relative h-full flex flex-col justify-center p-8 lg:p-12">
        <div className="max-w-lg mx-auto">
          {/* Agent status card */}
          <div className="mb-8 p-4" style={{ background: 'rgba(0,255,229,0.05)', border: '1px solid rgba(0,255,229,0.3)' }}>
            <div className="flex items-center gap-3">
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: CYAN, boxShadow: `0 0 8px ${CYAN}`, animation: 'pulse 2s ease-in-out infinite' }} />
              <span style={{ fontFamily: MONO, fontSize: '0.65rem', color: CYAN, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                {currentSlideData?.agent?.name} — STATUS: {currentSlideData?.agent?.status}
              </span>
            </div>
            <p style={{ fontFamily: MONO, fontSize: '0.8rem', color: GOLD, marginTop: '8px', paddingLeft: '20px' }}>
              &gt; "{currentSlideData?.agent?.message}"
            </p>
          </div>

          {/* Content */}
          <div className="space-y-6">
            <div>
              <h2 style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: '1.8rem', color: ICE, marginBottom: '1rem', lineHeight: 1.2 }}>
                {currentSlideData?.title}
              </h2>
              <p style={{ fontSize: '0.85rem', color: MUTED, lineHeight: 1.7, fontFamily: MONO }}>
                {currentSlideData?.description}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {currentSlideData?.features?.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <div style={{ width: '6px', height: '6px', background: CYAN, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.75rem', color: ICE, fontFamily: MONO }}>{feature}</span>
                </div>
              ))}
            </div>

            <div className="flex justify-start space-x-8 pt-4" style={{ borderTop: '1px solid rgba(0,255,229,0.15)' }}>
              {[
                { val: '82+', label: 'AI AGENTS' },
                { val: '10K+', label: 'OPERATORS' },
                { val: '95%', label: 'SUCCESS' },
              ]?.map(stat => (
                <div key={stat?.label}>
                  <p style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: '1.4rem', color: CYAN }}>{stat?.val}</p>
                  <p style={{ fontSize: '0.6rem', color: MUTED, fontFamily: MONO, letterSpacing: '0.1em' }}>{stat?.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {showcaseSlides?.map((_, index) => (
            <button key={index} onClick={() => setCurrentSlide(index)}
              style={{ width: index === currentSlide ? '20px' : '8px', height: '3px', background: index === currentSlide ? CYAN : 'rgba(0,255,229,0.3)', border: 'none', cursor: 'pointer', transition: 'all 0.3s' }} />
          ))}
        </div>

        {/* Navigation */}
        <button onClick={() => setCurrentSlide((prev) => (prev - 1 + showcaseSlides?.length) % showcaseSlides?.length)}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 w-8 h-8 flex items-center justify-center transition-all"
          style={{ background: 'rgba(0,255,229,0.08)', border: '1px solid rgba(0,255,229,0.3)', color: CYAN, cursor: 'pointer' }}>
          <Icon name="ChevronLeft" size={16} />
        </button>
        <button onClick={() => setCurrentSlide((prev) => (prev + 1) % showcaseSlides?.length)}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 w-8 h-8 flex items-center justify-center transition-all"
          style={{ background: 'rgba(0,255,229,0.08)', border: '1px solid rgba(0,255,229,0.3)', color: CYAN, cursor: 'pointer' }}>
          <Icon name="ChevronRight" size={16} />
        </button>
      </div>
    </div>
  );
};

export default AIShowcase;